import classNames from "classnames";
import * as React from "react";
import { Link } from "react-router-dom";
import AnimatedDialog from "../AnimatedDialog/AnimatedDialog";
import NumPadView from "../NumPad/NumPadView";

// tslint:disable-next-line:no-var-requires
const styles = require("./AnimatedLimitDialog.scss");

const InputType = {
    button: "button",
    text: "text"
};

type NewButton = {
    index: number;
    key: string;
    translation: string;
};

interface AnimatedDialogProps {
    title: string;
    text?: string;
    renderDialogHook: () => boolean;
    handleInputClick: (e: React.BaseSyntheticEvent | string) => void;
    buttons: string[] | NewButton[];
    isDesiredPayinLimitTooHigh?: boolean;
    exclusion?: boolean;
}

/* tslint:disable:jsx-no-multiline-js */
export const AnimatedLimitDialog = ({
    title,
    buttons,
    handleInputClick,
    renderDialogHook,
    exclusion
}: AnimatedDialogProps) => {
    const { submitInput, closeDialog, dialogVisible } = useDialog(
        handleInputClick,
        renderDialogHook
    );

    if (!exclusion) {
        buttons = buttons as string[];
        return (
            <AnimatedDialog
                visible={dialogVisible}
                title={title}
                maskClosable={true}
                onClose={closeDialog}
                className={classNames(styles.wrapper, styles.selectDialog)}
            >
                <div className={styles.content}>
                    {buttons.map((button, index) => (
                        <input
                            id={JSON.stringify(index)}
                            onClick={submitInput}
                            className={styles.btn}
                            type={InputType.button}
                            key={button}
                            value={button}
                        />
                    ))}
                </div>
            </AnimatedDialog>
        );
    }

    const renderButtons = () => {
        buttons = buttons as NewButton[];
        return buttons.map(button => (
            <input
                data-index={button.index}
                data-period={button.key}
                onClick={submitInput}
                className={styles.btn}
                type={InputType.button}
                key={button.key}
                value={button.translation}
            />
        ));
    };

    return (
        <AnimatedDialog
            visible={dialogVisible}
            title={title}
            maskClosable={true}
            onClose={closeDialog}
            className={classNames(styles.wrapper, styles.selectDialog)}
        >
            <div className={styles.content}>{renderButtons()}</div>
        </AnimatedDialog>
    );
};

export const AnimatedDisclaimerDialog = ({
    buttonCaptions,
    title,
    text,
    renderDialogHook,
    handleInputClick,
    handleSaveClick,
    isDesiredPayinLimitTooHigh
}: AnimatedDialogProps & {
    handleSaveClick: () => void;
    buttonCaptions: [string, string, string];
}) => {
    const { closeDialog, dialogVisible } = useDialog(
        handleInputClick,
        renderDialogHook
    );
    const saveWrapper = () => {
        handleSaveClick();
        closeDialog();
    };

    return (
        <AnimatedDialog
            className={styles.disclaimerDialog}
            visible={dialogVisible}
            title={title}
        >
            <div className={styles.disclaimerMessage}>{text}</div>
            <div className={styles.promptContainer}>
                {isDesiredPayinLimitTooHigh ? (
                    <Link to="/edd">
                        <button className={styles.promptBtn}>
                            {buttonCaptions[2]}
                        </button>
                    </Link>
                ) : (
                    <button className={styles.promptBtn} onClick={saveWrapper}>
                        {buttonCaptions[0]}
                    </button>
                )}

                <button className={styles.promptBtn} onClick={closeDialog}>
                    {buttonCaptions[1]}
                </button>
            </div>
        </AnimatedDialog>
    );
};

export const AnimatedLimitNumpadDialog = ({
    title,
    handleInputClick,
    renderDialogHook
}: AnimatedDialogProps) => {
    const { submitInput, closeDialog, dialogVisible } = useDialog(
        handleInputClick,
        renderDialogHook
    );
    return (
        <AnimatedDialog
            visible={dialogVisible}
            title={title}
            maskClosable={true}
            onClose={closeDialog}
            className={classNames(styles.wrapper, styles.inputDialog)}
        >
            <NumPadView submit={submitInput} />
        </AnimatedDialog>
    );
};

export const useDialog = (
    submitCallback: (e: React.BaseSyntheticEvent | string) => void,
    upstreamRenderHook: () => boolean
) => {
    const [dialogVisible, setDialogVisible] = React.useState<boolean>(false);

    React.useEffect(() => {
        setDialogVisible(upstreamRenderHook());
    }, [upstreamRenderHook]);

    const closeDialog = () => {
        setDialogVisible(false);
    };

    const submitInput = async (e: React.BaseSyntheticEvent | string) => {
        await submitCallback(e);
        closeDialog();
    };

    return {
        closeDialog,
        dialogVisible,
        submitInput
    };
};
